import { useId, useState, type CSSProperties } from 'react';
import { cn, usePrefersReducedMotion } from '@/lib/utils';
import { LETTER_LAYOUT as layout, LETTER_MASKS as MASKS, type LetterId } from './letters';
import './WordmarkPlayful.css';

/** Os estilos do hover, na ordem em que cada letra os percorre. */
export const LETTER_STYLES = [
  'pontilhada',
  'contorno',
  'turquesa',
  'italica',
  'rabisco',
  'gradiente',
] as const;
export type LetterStyle = (typeof LETTER_STYLES)[number];

// O rabisco é o único estilo que se mexe sozinho; com menos movimento ele sai da roda.
const CALM_STYLES = LETTER_STYLES.filter((style) => style !== 'rabisco');

// Quatro sementes de ruído trocadas em degraus: o traço "treme" como desenhado à mão.
const SQUIGGLE_SEEDS = [1, 2, 3, 4];

export interface WordmarkPlayfulProps {
  className?: string;
  label?: string;
}

/**
 * O wordmark ∫hibΔ letra a letra: em repouso é a marca cheia; ao passar o mouse
 * numa letra ela vira o próximo estilo da sua roda, e cada letra começa a roda
 * num ponto diferente, para os estilos se alternarem pela palavra. As letras são
 * máscaras recortadas do raster-fonte (scripts/recortar_letras_marca.py),
 * posicionadas pelos offsets originais para manter o kerning. A largura vem do
 * CSS de quem usa; a altura sai da proporção do raster.
 */
export function WordmarkPlayful({ className, label = '∫hibΔ' }: WordmarkPlayfulProps) {
  const filterPrefix = `wordmark-rabisco-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const reduceMotion = usePrefersReducedMotion();
  const [hovered, setHovered] = useState<number | null>(null);
  // Quantas vezes cada letra já foi visitada: é o passo dela na roda.
  const [visits, setVisits] = useState<number[]>(() => layout.letras.map(() => 0));

  const wheel = reduceMotion ? CALM_STYLES : LETTER_STYLES;
  const styleOf = (index: number): LetterStyle | undefined =>
    hovered === index ? wheel[(index + (visits[index] ?? 1) - 1) % wheel.length] : undefined;

  const enter = (index: number) => {
    setHovered(index);
    setVisits((current) => current.map((visit, n) => (n === index ? visit + 1 : visit)));
  };
  const leave = (index: number) => {
    setHovered((current) => (current === index ? null : current));
  };

  const squiggles = Object.fromEntries(
    SQUIGGLE_SEEDS.map((seed) => [`--rabisco-${seed}`, `url(#${filterPrefix}-${seed})`]),
  );

  return (
    <span
      role="img"
      aria-label={label}
      className={cn('wordmark-playful', className)}
      style={{ aspectRatio: `${layout.largura} / ${layout.altura}`, ...squiggles }}
    >
      <svg className="wordmark-playful__filters" aria-hidden="true" focusable="false">
        {SQUIGGLE_SEEDS.map((seed) => (
          <filter key={seed} id={`${filterPrefix}-${seed}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves={3} seed={seed} />
            <feDisplacementMap in="SourceGraphic" scale={6} />
          </filter>
        ))}
      </svg>
      {layout.letras.map((letter, index) => {
        const style = styleOf(index);
        // O JSON sai do script com estes ids, na ordem da palavra.
        const masks = MASKS[letter.id as LetterId];
        return (
          <span
            key={letter.id}
            aria-hidden="true"
            data-letter={letter.id}
            data-style={style ?? 'cheia'}
            className={cn('wordmark-playful__letter', style && `wordmark-playful__letter--${style}`)}
            style={
              {
                left: `${(letter.x / layout.largura) * 100}%`,
                width: `${(letter.largura / layout.largura) * 100}%`,
                '--letter-mask': `url(${style === 'contorno' ? masks.contorno : masks.cheia})`,
              } as CSSProperties
            }
            onPointerEnter={() => enter(index)}
            onPointerLeave={() => leave(index)}
          />
        );
      })}
    </span>
  );
}
